import { useState, useEffect } from 'react';

import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
  IconButton,
  TextField,
  Button,
  Box,
} from '@mui/material';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import HeaderInput from './HeaderInput';
import VariableInterpolationHelp from './VariableInterpolationHelp';

import type { HTTPData, Header } from '../dataProvider/types';

export const getUrlValidationMessage = (url: string): string => {
  const reg = new RegExp(urlValidationPattern);
  if (url === '') {
    flowchartLogger.debug('Validation: URL is empty');
    return 'callflows.nodes.http.urlErrorEmpty';
  }
  if (!reg.test(url)) {
    flowchartLogger.debug('Validation: URL is invalid', url);
    return 'callflows.nodes.http.urlErrorInvalid';
  }
  flowchartLogger.debug('Validation: URL is valid', url);
  return '';
};

interface HTTPInputProps {
  updateHTTPInputData: (data: HTTPData) => void;
  data: HTTPData;
}

const urlValidationPattern = 'https?://.+';

const HTTPInput = ({ data, updateHTTPInputData }: HTTPInputProps): React.ReactElement => {
  const [url, setUrl] = useState<string>(data.url ?? '');
  const [urlHelperText, setUrlHelperText] = useState<string>('');
  const [method, setMethod] = useState<'get' | 'post'>(data.method ?? 'get');
  const [headers, setHeaders] = useState<Header[]>(data.headers ?? []);
  const [sslVerify, setSSLVerify] = useState<boolean>(data.sslVerify ?? true);
  const translate = useTranslate();

  useEffect(() => {
    updateHTTPInputData({
      url,
      method,
      headers,
      sslVerify,
    });
  }, [url, method, headers, sslVerify, updateHTTPInputData]);

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setUrlHelperText(translate(getUrlValidationMessage(event.target.value)));
    setUrl(event.target.value);
  };

  const addHeader = (): void => {
    const lastHeader = headers[headers.length - 1];
    flowchartLogger.debug('Adding header after', lastHeader);
    if (headers.length < 1 || lastHeader?.name !== '' || lastHeader?.value !== '') {
      flowchartLogger.debug('Header added');
      setHeaders((prevHeaders) => [...prevHeaders, { name: '', value: '' }]);
    }
  };

  const updateHeader = (index: number, header: Header): void => {
    flowchartLogger.debug('Updating header at index', index, 'with value', header);
    const updatedHeaders = [...headers];
    updatedHeaders[index] = header;
    setHeaders(updatedHeaders);
  };

  const deleteHeader = (index: number): void => {
    flowchartLogger.debug('Deleting header at index', index);
    const updatedHeaders = [...headers];
    updatedHeaders.splice(index, 1);
    setHeaders(updatedHeaders);
  };

  return (
    <FormControl sx={{ display: 'flex', flexDirection: 'column', gap: 1, overflow: 'auto' }} data-testid='http-input'>
      <FormHelperText sx={{ display: 'flex', alignItems: 'center' }}>
        {translate('callflows.nodes.http.urlDescription')}
        <VariableInterpolationHelp />
      </FormHelperText>
      <TextField
        type='url'
        value={url}
        slotProps={{ htmlInput: { pattern: urlValidationPattern } }}
        placeholder='https://example.com'
        onChange={handleUrlChange}
        error={urlHelperText !== ''}
        helperText={urlHelperText}
        required
      />
      <>
        <FormHelperText>{translate('callflows.nodes.http.methodDescription')}</FormHelperText>
        <InputLabel id='http-controls-method'></InputLabel>
        <Select
          required
          variant='outlined'
          labelId='http-controls-method'
          value={method}
          label={translate('callflows.nodes.http.method')}
          onChange={(event) => {
            event.target.value === 'post' ? setMethod('post') : setMethod('get');
          }}
          sx={{ mb: 1 }}
        >
          <MenuItem value='get'>{translate('callflows.nodes.http.methodGet')}</MenuItem>
          <MenuItem value='post'>{translate('callflows.nodes.http.methodPost')}</MenuItem>
        </Select>
      </>
      <FormControlLabel
        control={
          <Checkbox
            checked={sslVerify}
            onChange={() => {
              setSSLVerify(!sslVerify);
            }}
          />
        }
        label={translate('callflows.nodes.http.sslVerify')}
      />
      <FormHelperText>
        {translate('callflows.nodes.http.headers')}
        <Tooltip title={translate('callflows.nodes.http.headersTooltip')}>
          <IconButton>
            <InfoRoundedIcon />
          </IconButton>
        </Tooltip>
      </FormHelperText>
      <Box display='flex' flexDirection='column' gap={1} minHeight={12.5} overflow='auto' pt={1}>
        {headers.map((header, index) => (
          <HeaderInput
            // Can be disabled as we are not reordering headers:
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            header={header}
            onChange={(updatedHeader: Header) => {
              updateHeader(index, updatedHeader);
            }}
            onDelete={() => {
              deleteHeader(index);
            }}
          />
        ))}
      </Box>
      <Button variant='outlined' onClick={addHeader}>
        {translate('callflows.nodes.http.addHeader')}
      </Button>
    </FormControl>
  );
};

export default HTTPInput;
